import React, { useState, useEffect } from "react"
import {Edit2, Save, X} from 'lucide-react'
import { lumi } from "../lib/lumi"
import { ReviewerMode } from "./ReviewerMode"
import { ReviewerMode } from "./ReviewerMode"

interface ContentItem {
  _id: string
  section: string
  key: string
  value: string
  creator: string
  createdAt: string
  updatedAt: string
}

interface ContentEditorProps {
  section: string
  contentKey: string
  defaultValue: string
  className?: string
  as?: "h1" | "h2" | "h3" | "p" | "span"
}

export function ContentEditor({
  section,
  contentKey,
  defaultValue,
  className = "",
  as: Component = "p"
}: ContentEditorProps) {
  const [content, setContent] = useState<ContentItem | null>(null)
  const [editValue, setEditValue] = useState("")
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [adminMode, setAdminMode] = useState<'editor' | 'reviewer'>(
    (localStorage.getItem('adminMode') as 'editor' | 'reviewer') || 'editor'
  )

  const [user, setUser] = useState(lumi.auth.user)
  
  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange(({ user }) => {
      setUser(user)
    })
    return unsubscribe
  }, [])
  
  const isAdmin = user?.userRole === "ADMIN"
  const isReviewer = user?.userRole === "USER"
  
  // Admin en mode relecteur se comporte comme un relecteur
  const canEdit = isAdmin && adminMode === 'editor'
  const canReview = (isAdmin && adminMode === 'reviewer') || (isReviewer && !isAdmin)

  useEffect(() => {
    fetchContent()
  }, [section, contentKey])

  useEffect(() => {
    const handleModeChange = (e: CustomEvent) => {
      setAdminMode(e.detail)
      setIsEditing(false)
    }
    window.addEventListener('adminModeChange', handleModeChange as EventListener)
    return () => window.removeEventListener('adminModeChange', handleModeChange as EventListener)
  }, [])

  const fetchContent = async () => {
    try {
      const { list } = await lumi.entities.content.list({
        filter: { section, key: contentKey },
        limit: 1
      })

      if (list.length > 0) {
        setContent(list[0])
        setEditValue(list[0].value)
      } else {
        setEditValue(defaultValue)
      }
    } catch (error) {
      console.error("Failed to fetch content:", error)
      setEditValue(defaultValue)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!canEdit) return

    setSaving(true)
    try {
      if (content?._id) {
        await lumi.entities.content.update(content._id, {
          value: editValue,
          updatedAt: new Date().toISOString()
        })
      } else {
        await lumi.entities.content.create({
          section,
          key: contentKey,
          value: editValue,
          creator: user?.userId || "admin",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
      }

      await fetchContent()
      setIsEditing(false)
    } catch (error) {
      console.error("Failed to save content:", error)
      alert("Échec de la sauvegarde. Veuillez réessayer.")
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setEditValue(content?.value || defaultValue)
    setIsEditing(false)
  }

  if (loading) {
    return <Component className={`${className} whitespace-pre-line`}>{defaultValue}</Component>
  }

  const displayValue = content?.value || defaultValue

  if (!canEdit && !canReview) {
    return <Component className={`${className} whitespace-pre-line`}>{displayValue}</Component>
  }

  if (canReview) {
    return (
      <div className="relative inline-block w-full">
        <div className="group relative">
          <Component className={`${className} whitespace-pre-line`}>{displayValue}</Component>
          <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <ReviewerMode
              section={section}
              contentKey={contentKey}
              currentValue={displayValue}
              elementType="text"
            />
          </div>
        </div>
      </div>
    )
  }

  if (isEditing) {
    return (
      <div className="relative group">
        {Component === "h1" || Component === "h2" || Component === "h3" ? (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className={`${className} w-full border-2 border-teal-500 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600 bg-white`}
            autoFocus
          />
        ) : (
          <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className={`${className} w-full border-2 border-teal-500 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600 bg-white min-h-[100px]`}
            autoFocus
          />
        )}

        <div className="flex gap-2 mt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? "Sauvegarde..." : "Sauvegarder"}
          </button>
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
          >
            <X className="w-4 h-4" />
            Annuler
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="relative inline-block w-full">
      <div className="group relative">
        <Component className={`${className} whitespace-pre-line`}>{displayValue}</Component>
        {canEdit && (
          <button
            onClick={() => setIsEditing(true)}
            className="absolute left-0 top-0 w-full h-full opacity-0 group-hover:opacity-10 bg-teal-600 rounded transition-opacity"
          />
        )}
        {canEdit && (
          <button
            onClick={() => setIsEditing(true)}
            className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity bg-teal-600 text-white p-2 rounded-lg hover:bg-teal-700 shadow-lg z-10"
            title="Modifier ce contenu"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}
